import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Activity, ArrowUpRight, BarChart3, RefreshCw, Sparkles, Users, Zap } from "lucide-react";
import { ADVERTISEMENT_URL } from "../../utils/billing";

const API_BASE = "https://api.testoraglobal.in/api";
const METRICS = [
  {
    key: "ai_cycles",
    label: "Test Cases Generator",
    icon: Sparkles,
    iconColor: "text-purple-400",
    iconBackground: "bg-purple-500/15",
    barColor: "bg-purple-500",
  },
  {
    key: "executions",
    label: "Test Executions",
    icon: Zap,
    iconColor: "text-cyan-400",
    iconBackground: "bg-cyan-500/15",
    barColor: "bg-cyan-500",
  },
  {
    key: "api_requests",
    label: "API Requests",
    icon: Activity,
    iconColor: "text-emerald-400",
    iconBackground: "bg-emerald-500/15",
    barColor: "bg-emerald-500",
  },
  {
    key: "seats",
    label: "Seats",
    icon: Users,
    iconColor: "text-amber-400",
    iconBackground: "bg-amber-500/15",
    barColor: "bg-amber-500",
  },
];
const PLAN_FEATURES = [
  ["seats", "Seats"],
  ["projects", "Projects"],
  ["ai_cycles", "Test Cases Generator"],
  ["executions", "Test Executions"],
  ["api_requests", "API Requests"],
];

function formatLimit(planName, value) {
  if (value !== null && value !== undefined) return value.toLocaleString();
  return planName === "Scale" ? "Custom" : "Unlimited";
}

export default function UsageSettings({ darkMode }) {
  const [usageData, setUsageData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_BASE}/org/usage`);
      setUsageData(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load organization usage.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const panel = darkMode
    ? "border-gray-800 bg-[#0B1220]"
    : "border-slate-200 bg-white";
  const subtlePanel = darkMode
    ? "border-gray-800 bg-[#111827]"
    : "border-slate-200 bg-slate-50";
  const muted = darkMode ? "text-gray-400" : "text-slate-500";

  if (loading && !usageData) {
    return (
      <div className={`flex min-h-64 items-center justify-center rounded-3xl border ${panel}`}>
        <RefreshCw className="animate-spin text-purple-500" size={26} />
      </div>
    );
  }

  if (error && !usageData) {
    return (
      <div className={`rounded-3xl border p-7 ${panel}`}>
        <p className="font-semibold text-rose-400">{error}</p>
        <button
          type="button"
          onClick={loadUsage}
          className="mt-4 rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-500"
        >
          Try again
        </button>
      </div>
    );
  }

  const currentPlan = usageData?.plan_name || "Starter";
  const plans = usageData?.plans || {};

  return (
    <div className="space-y-8">
      <section className={`rounded-3xl border p-6 shadow-sm sm:p-8 ${panel}`}>
        <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-purple-400">
              <BarChart3 size={19} />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">Organization usage</span>
            </div>
            <h2 className="mt-2 text-2xl font-bold">Current Usage</h2>
            <p className={`mt-1 text-sm ${muted}`}>
              {usageData?.org_name || "Your organization"} · {currentPlan} plan
            </p>
          </div>
          <button
            type="button"
            onClick={loadUsage}
            disabled={loading}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition disabled:opacity-50 ${darkMode ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {error && (
          <p role="status" className="mb-5 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-400">
            {error}
          </p>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {METRICS.map(({ key, label, icon: Icon, iconColor, iconBackground, barColor }) => {
            const used = usageData?.usage?.[key] || 0;
            const limit = usageData?.limits?.[key];
            const hasLimit = Number.isFinite(limit);
            const percentage = hasLimit && limit > 0
              ? Math.round((used / limit) * 100)
              : null;

            return (
              <article key={key} className={`rounded-2xl border p-5 ${subtlePanel}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBackground}`}>
                      <Icon size={19} className={iconColor} />
                    </span>
                    <div>
                      <h3 className="font-semibold">{label}</h3>
                      <p className={`mt-0.5 text-xs ${muted}`}>
                        {used.toLocaleString()} used
                        {hasLimit ? ` of ${limit.toLocaleString()}` : " · custom limit"}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-bold tabular-nums">
                    {percentage === null ? "Custom" : `${percentage}%`}
                  </span>
                </div>
                <div
                  className={`mt-5 h-2.5 overflow-hidden rounded-full ${darkMode ? "bg-gray-800" : "bg-slate-200"}`}
                  role="progressbar"
                  aria-label={`${label} usage`}
                  aria-valuemin={0}
                  aria-valuemax={hasLimit ? 100 : undefined}
                  aria-valuenow={percentage === null ? undefined : Math.min(percentage, 100)}
                  aria-valuetext={percentage === null ? `${used} used, custom limit` : `${percentage}% used`}
                >
                  {percentage !== null && (
                    <div
                      className={`h-full rounded-full transition-all ${barColor}`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  )}
                  {percentage === null && (
                    <div className="h-full w-full rounded-full bg-gradient-to-r from-purple-500/30 via-cyan-500/30 to-emerald-500/30" />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className={`rounded-3xl border p-6 shadow-sm sm:p-8 ${panel}`}>
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-purple-400">Plans</p>
          <h2 className="mt-2 text-2xl font-bold">Available Plans</h2>
          <p className={`mt-1 text-sm ${muted}`}>Compare allowances and choose the plan that fits your team.</p>
        </div>

        <div className="grid gap-4 xl:grid-cols-4">
          {Object.entries(plans).map(([planName, limits]) => {
            const isCurrentPlan = planName === currentPlan;
            return (
              <article
                key={planName}
                className={`flex flex-col rounded-2xl border p-5 ${isCurrentPlan ? "border-purple-500/60 bg-purple-500/5 ring-1 ring-purple-500/20" : subtlePanel}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-lg font-bold">{planName}</h3>
                  {isCurrentPlan && (
                    <span className="rounded-full bg-purple-500/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-purple-400">
                      Current
                    </span>
                  )}
                </div>
                <ul className={`mt-5 flex-1 space-y-3 text-sm ${muted}`}>
                  {PLAN_FEATURES.map(([key, label]) => (
                    <li key={key} className="flex items-center justify-between gap-3">
                      <span>{label}</span>
                      <span className={`font-semibold ${darkMode ? "text-gray-200" : "text-slate-800"}`}>
                        {formatLimit(planName, limits[key])}
                      </span>
                    </li>
                  ))}
                </ul>
                {isCurrentPlan ? (
                  <div className="mt-6 rounded-xl border border-purple-500/20 bg-purple-500/10 px-3 py-2.5 text-center text-sm font-semibold text-purple-400">
                    Your current plan
                  </div>
                ) : (
                  <a
                    href={ADVERTISEMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-purple-500"
                  >
                    Upgrade Now <ArrowUpRight size={15} />
                  </a>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
